import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

/** Image hotspots: tooltip on desktop, bottom drawer below 750px. */
class ShoppableImage extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.querySelector('dialog-component')?.addEventListener(DialogCloseEvent.eventName, this.#closeAll);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#closeAll);
    mediaQueryLarge.addEventListener('change', this.#closeAll);

    this.#reveal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.querySelector('dialog-component')?.removeEventListener(DialogCloseEvent.eventName, this.#closeAll);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#closeAll);
    mediaQueryLarge.removeEventListener('change', this.#closeAll);
    this.#observer?.disconnect();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const hotspot = event.target.closest('[data-hotspot-trigger]')?.closest('[data-hotspot]');
    if (!(hotspot instanceof HTMLElement)) return;

    if (mediaQueryLarge.matches) {
      this.#toggle(hotspot);
    } else {
      this.#openDrawer(hotspot);
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!mediaQueryLarge.matches || !(event.target instanceof Element)) return;
    if (event.target.closest('[data-hotspot-trigger], [data-hotspot-tooltip]')) return;

    this.#closeAll();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const open = this.querySelector('[data-hotspot].is-open');
    if (!open || !mediaQueryLarge.matches) return;

    this.#closeAll();
    /** @type {HTMLElement | null} */ (open.querySelector('[data-hotspot-trigger]'))?.focus();
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const hotspot = event.target instanceof Element ? event.target.closest('[data-hotspot]') : null;
    if (!(hotspot instanceof HTMLElement) || !this.contains(hotspot) || !mediaQueryLarge.matches) return;

    this.#closeAll();
    this.#setOpen(hotspot, true);
  };

  /** @param {HTMLElement} hotspot */
  #toggle(hotspot) {
    const wasOpen = hotspot.classList.contains('is-open');

    this.#closeAll();
    if (!wasOpen) this.#setOpen(hotspot, true);
  }

  /**
   * @param {HTMLElement} hotspot
   * @param {boolean} open
   */
  #setOpen(hotspot, open) {
    hotspot.classList.toggle('is-open', open);
    hotspot.querySelector('[data-hotspot-trigger]')?.setAttribute('aria-expanded', String(open));
  }

  #closeAll = () => {
    for (const hotspot of this.querySelectorAll('[data-hotspot].is-open')) {
      if (hotspot instanceof HTMLElement) this.#setOpen(hotspot, false);
    }
  };

  /** @param {HTMLElement} hotspot */
  #openDrawer(hotspot) {
    const drawer = /** @type {import('@theme/dialog').DialogComponent | null} */ (
      this.querySelector('dialog-component')
    );
    const body = drawer?.querySelector('[data-drawer-body]');
    const content = hotspot.querySelector('[data-hotspot-content]');
    if (!drawer || !body || !content) return;

    body.replaceChildren(content.cloneNode(true));
    this.#closeAll();
    this.#setOpen(hotspot, true);
    drawer.showDialog();
  }

  // Desktop-only slide-in as the image scrolls into view.
  #reveal() {
    const media = this.querySelector('[data-hotspot-media]');
    if (!media || !mediaQueryLarge.matches || prefersReducedMotion() || window.Shopify?.designMode) return;

    media.setAttribute('data-offscreen', '');
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;

        media.removeAttribute('data-offscreen');
        this.#observer?.disconnect();
      },
      { rootMargin: '0px 0px -50px 0px' }
    );
    this.#observer.observe(media);
  }
}

if (!customElements.get('shoppable-image')) {
  customElements.define('shoppable-image', ShoppableImage);
}
